import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { CurrencyCode } from "models/enums/currency";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { KeyValue } from "models/utilTypes/keyValue";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  DetailsTableViewSmall,
  LoadingWithLogo,
  PageLayouts,
  RouteBreadCrumb,
  StaticInfoMessage,
  TitleSmall,
  ViewPanel,
} from "components/common";
import { Grid, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { ErrorView } from "components/error";
import useVatReportPermissions from "./usePermissions";
import VatReportsAvailabilityFailed from "./availabilityFailed";
import VatReportStatusTag from "./statusTag";
import VatReportDueDate from "./dueDate";
import VatReportStateFlow from "./stateFlow";
import { useVatReportService } from "serviceHooks";
//#endregion
import {
  formatVatDate,
  formatVatPeriod,
  getVatAmountDirection,
  getVatReportActionReasonKey,
  groupVatReportBoxes,
  isVatReportTotalBox,
  showsVatReportLifecycle,
} from "./vatReportModel";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

type DetailsItem = KeyValue<string, string | React.ReactElement>;

const MISSING = "–";

const formatAmount = (amount?: number) =>
  amount === undefined || amount === null
    ? MISSING
    : LocalizationHelperInstance.getFormattedCurrency(amount, CurrencyCode.SEK);

// DetailsTable draws a separator row wherever it is told to, so one between every pair of present
// rows keeps a hidden row (no voucher yet) from leaving a doubled line behind.
const withSeparators = (items: (DetailsItem | undefined)[]): (DetailsItem | typeof ACTION_SEPARATOR)[] =>
  items
    .filter((item): item is DetailsItem => item !== undefined)
    .flatMap((item, index) => (index === 0 ? [item] : [ACTION_SEPARATOR, item]));

/**
 * One VAT report, read-only: the declaration's boxes grouped like Skatteverket's form, with the
 * amount to pay and the report's details beside them.
 *
 * There is no source document to show and nothing to act on, so the boxes take the main column and
 * there is no footer; the lifecycle row on this DashboardLayout page is a flagged design deviation.
 */
const VatReportDetailView: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { access, retryAvailability } = useVatReportPermissions();
  const { getReport } = useVatReportService();

  const [loaded, setLoaded] = React.useState<{ id: string; report?: WintSuperkollAPIModel.VatReportDetails }>();

  const granted = access === "granted";

  React.useEffect(() => {
    // Without a yes there is nothing to ask for: anyone else would only collect a 403.
    if (!granted) return;

    // React Router keeps this component when only `:id` changes, so an earlier report answering last
    // must not land under the later report's URL.
    let mounted = true;
    getReport(id)
      .then((report) => {
        if (mounted) setLoaded({ id, report });
      })
      // A 404 (an id that is not this company's, or reports not shown for it) and any other failure end
      // on the error view below; there is nothing on this page to show without the report.
      .catch(() => {
        if (mounted) setLoaded({ id });
      });

    return () => {
      mounted = false;
    };
    // `getReport` is fresh each render, so including it would loop.
  }, [id, granted]);

  const pageName = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReport);

  // The permission answer comes first: the error view below means "this report", not "no access".
  if (access === "checking") return <LoadingWithLogo />;
  if (access === "failed") return <VatReportsAvailabilityFailed title={pageName} onRetry={retryAvailability} />;
  if (access === "denied") return <PageLayouts.DashboardLayout entityKey={0} hasPermissions={false} title={pageName} />;
  // Until this id has answered, the report held is another one's.
  if (loaded?.id !== id) return <LoadingWithLogo />;

  const report = loaded.report;
  if (!report) return <ErrorView />;

  const period = formatVatPeriod(report.start, report.end, LocalizationHelperInstance.intl.locale);
  const actionReason = getVatReportActionReasonKey(report.status, report.actionReason);
  const amountDirection = getVatAmountDirection(report.amountToPay);
  const additionalInformation = report.additionalInformation?.trim();

  const title = (
    <Grid container spacing={2} alignItems="center">
      <Grid item className={cssExports.dashboardHeaderText}>
        {period}
      </Grid>
      <Grid item>
        <VatReportStatusTag status={report.status} filedByCustomer={report.filedByCustomer} />
      </Grid>
    </Grid>
  );

  const details = withSeparators([
    new KeyValue(LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportVatPeriod), period),
    report.dueDate
      ? new KeyValue(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Due_Date),
          <VatReportDueDate dueDate={report.dueDate} isOverdue={report.isOverdue} />
        )
      : undefined,
    new KeyValue(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Status),
      <VatReportStatusTag status={report.status} filedByCustomer={report.filedByCustomer} />
    ),
    new KeyValue(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportCorrection),
      LocalizationHelperInstance.getFormattedMessage(
        report.isCorrection ? TranslationKeys.Common.Yes : TranslationKeys.Common.No
      )
    ),
    report.voucherNumber
      ? new KeyValue(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportVoucher),
          report.voucherNumber
        )
      : undefined,
  ]);

  return (
    <PageLayouts.DashboardLayout
      entityKey={0}
      hasPermissions={granted}
      title={title}
      // A `:id` segment never matches a route config entry, so the view names the leaf itself.
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={period} />}
    >
      <Grid container rowGap={3} columnSpacing={3}>
        {showsVatReportLifecycle(report.status, report.filedByCustomer) && (
          <Grid item xs={12}>
            <VatReportStateFlow status={report.status} />
          </Grid>
        )}

        <Grid item md={8} xs={12}>
          <Grid container direction="column" rowGap={2}>
            {report.isOverdue && report.dueDate && (
              <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning} fullWidth surface="background">
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportOverdueMessage, "", {
                  date: formatVatDate(report.dueDate),
                })}
              </StaticInfoMessage>
            )}
            {actionReason && (
              <StaticInfoMessage displayType={StyleEnums.DisplayType.Danger} fullWidth surface="background">
                {LocalizationHelperInstance.getTranslatedEnumValue("Advisor.VatReportActionReason", actionReason)}
              </StaticInfoMessage>
            )}
            <ViewPanel
              className={cssExports.advisorVatReportPanel}
              icon={IconDefinitionConstants.WVat}
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportDeclaration)}
            >
              <Grid container direction="column" rowGap={3}>
                {groupVatReportBoxes(report.boxes).map((section) => (
                  <Grid item container direction="column" rowGap={1} key={section.key ?? "other"}>
                    {section.key && <TitleSmall title={`Advisor.VatReportSection_${section.key}`} />}
                    <DetailsTableViewSmall
                      valueAlignment="right"
                      detailsList={section.boxes.map((box) => {
                        const amount = formatAmount(box.amount);
                        return new KeyValue(
                          `${box.code ?? ""} ${box.description ?? ""}`.trim(),
                          // Box 49 is the declaration's result, so it reads as the total row.
                          isVatReportTotalBox(box.code) ? <strong>{amount}</strong> : amount
                        );
                      })}
                    />
                  </Grid>
                ))}
              </Grid>
            </ViewPanel>
          </Grid>
        </Grid>

        <Grid item md={4} xs={12}>
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <ViewPanel
                icon={IconDefinitionConstants.WMoney}
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportAmountToPay)}
              >
                <Grid container direction="column">
                  <Grid item className={cssExports.dashboardHeaderText}>
                    {formatAmount(report.amountToPay)}
                  </Grid>
                  <Grid item className={cssExports.caption}>
                    {[
                      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportAmountToPayBox),
                      amountDirection &&
                        LocalizationHelperInstance.getFormattedMessage(
                          amountDirection === "ToPay"
                            ? TranslationKeys.Advisor.VatReportToPay
                            : TranslationKeys.Advisor.VatReportToReceive
                        ),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Grid>
                </Grid>
              </ViewPanel>
            </Grid>

            <Grid item>
              <ViewPanel
                className={cssExports.advisorVatReportPanel}
                icon={IconDefinitionConstants.WInfo}
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
              >
                <DetailsTableViewSmall detailsList={details} />
              </ViewPanel>
            </Grid>

            {/* Hidden rather than shown empty: a panel holding only a placeholder reads as a failed load. */}
            {additionalInformation && (
              <Grid item>
                <ViewPanel
                  icon={IconDefinitionConstants.WCommentDots}
                  title={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Advisor.VatReportAdditionalInformation
                  )}
                >
                  <Typography variant="body2" className={cssExports.advisorVatReportAdditionalInformation}>
                    {additionalInformation}
                  </Typography>
                </ViewPanel>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default VatReportDetailView;
